import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { VibeCostSafetyActiveStatusSchema } from "../../../shared/domain/vibe/cost-safety-active-statuses";
import {
  VibeAuditCostSafetyActiveStatusReadSchema,
  VibeAuditDeploymentStatusReadSchema
} from "./read-vocabularies.schema";

/**
 * Emitted by the cost-safety rollback sweeper when it transitions a
 * non-terminal `VibeDeployment` (BUILDING / AWAITING_APPROVAL /
 * DEPLOYING / HEALTHY) to ROLLED_BACK because the owning org is in
 * `VibeOrgCostSafetyStatus.SUSPENDED`. Persisted INSIDE the per-row
 * `$transaction` together with the deployment UPDATE so the audit
 * row + the status flip land atomically — neither survives the
 * other. One audit row per actually-flipped deployment so the per-
 * app audit timeline picks up the rollback decisively (a single
 * bulk event would hide which app was affected).
 *
 * `priorStatus` captures the deployment's state at the moment the
 * sweeper saw the row — diagnostic for "did we cancel a build or
 * tear down a serving deployment?". The literal enum is derived from
 * `VibeCostSafetyActiveStatusSchema` so the audit payload's accepted
 * values stay in lockstep with the rollback sweeper's read filter +
 * race-loser re-narrowing — drift impossible. `triggerSha` carries
 * the commit so the user can re-trigger after admin lifts the
 * suspension without re-deriving the sha from log scraping.
 */
export const VibeAuditDeploymentRolledBackCostSafetyPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_ROLLED_BACK_COST_SAFETY"),
  vibeDeploymentId: z.string().uuid(),
  priorStatus: VibeCostSafetyActiveStatusSchema,
  triggerSha: z.string().min(1).max(64),
  /// Human-readable rationale carried through from the org's
  /// `VibeOrgCostSafetyState.suspendedReason` at sweep time, so the
  /// CLI can show the deployer-side message without re-querying the
  /// org's state. Null when the SUSPENDED row carries no reason
  /// (the admin write surface enforces a non-empty reason, but a
  /// future flow might land a SUSPENDED state from a different
  /// source without one).
  suspendedReason: z.string().nullable()
});
export type VibeAuditDeploymentRolledBackCostSafetyPayload = z.infer<
  typeof VibeAuditDeploymentRolledBackCostSafetyPayloadSchema
>;

/**
 * Emitted when a deployment flips to ROLLED_BACK because the executor's
 * post-deploy health check failed. The health-check callback webhook
 * writes this INSIDE the same `$transaction` as the deployment UPDATE.
 * Distinct from DEPLOYMENT_ROLLED_BACK_COST_SAFETY (a policy action by
 * the cost-safety sweeper) — this is an executor-reported health failure.
 *
 * `priorStatus` is diagnostic: DEPLOYING means the new instance set never
 * passed its health check (rolled back before going live); HEALTHY means
 * it went live then degraded. Typed as the full generated
 * `VibeDeploymentStatus` enum (drift-proof, no hardcoded literals) — the
 * entity's `markRolledBack` source-guard (DEPLOYING | HEALTHY) is the
 * enforcement layer, mirroring how DEPLOYMENT_FAILED handles `priorStatus`.
 */
export const VibeAuditDeploymentRolledBackHealthCheckPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_ROLLED_BACK_HEALTH_CHECK"),
  vibeDeploymentId: z.string().uuid(),
  priorStatus: z.enum(DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES),
  /// Health-check failure surface text shown by the CLI. No max length —
  /// capping would turn a rollback into a 500 on a long reason. Non-empty
  /// per the entity contract (`markRolledBack` requires `errorReason`).
  errorReason: z.string().min(1),
  /// Commit sha so the user can re-trigger after a fix without
  /// log-scraping.
  triggerSha: z.string().min(1).max(64)
});
export type VibeAuditDeploymentRolledBackHealthCheckPayload = z.infer<
  typeof VibeAuditDeploymentRolledBackHealthCheckPayloadSchema
>;

/**
 * Emitted when an operator rolls an app back to its previous version from
 * the console. In one status-guarded `$transaction` the currently-serving
 * deployment is retired (HEALTHY -> SUPERSEDED) and its last-healthy
 * predecessor is re-activated (SUPERSEDED -> DEPLOYING, its retained ECR
 * image re-placed by the agent). `actorUserId` carries the operator who
 * initiated it — unlike the system-actor rollbacks below, this is a
 * deliberate human action.
 *
 * Distinct from every other rollback / retirement event:
 *   - DEPLOYMENT_SUPERSEDED records a NEWER deploy cleanly replacing an
 *     older one via blue-green cutover; here an OLDER version deliberately
 *     takes over, so the two must read differently in the feed.
 *   - DEPLOYMENT_ROLLED_BACK_HEALTH_CHECK / _COST_SAFETY flip a row to the
 *     ROLLED_BACK status on a failure/policy trigger; a user rollback
 *     flips NO row to ROLLED_BACK — it supersedes + restores.
 *
 * The payload names both sides so the feed answers "what did the rollback
 * do?" from the row alone: `restored*` is the version brought back (now
 * DEPLOYING), `superseded*` the version retired (now SUPERSEDED).
 */
export const VibeAuditDeploymentRolledBackUserPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_ROLLED_BACK_USER"),
  /// The predecessor re-activated SUPERSEDED -> DEPLOYING — the version the
  /// app is rolling back TO. Its retained ECR image is what the agent
  /// re-places.
  restoredDeploymentId: z.string().uuid(),
  /// Commit sha of the restored version. Same bounds as the other
  /// deployment payloads' `triggerSha`.
  restoredTriggerSha: z.string().min(1).max(64),
  /// The previously-serving deployment retired HEALTHY -> SUPERSEDED in the
  /// same TX — the version the app is rolling back FROM.
  supersededDeploymentId: z.string().uuid(),
  /// Commit sha of the retired version.
  supersededTriggerSha: z.string().min(1).max(64)
});
export type VibeAuditDeploymentRolledBackUserPayload = z.infer<
  typeof VibeAuditDeploymentRolledBackUserPayloadSchema
>;

/**
 * Emitted when a deployment that was ALREADY SERVING crash-looped and the
 * platform pulled it, restoring its last-healthy predecessor. Written INSIDE
 * the same composite `$transaction` as both status flips (crasher
 * HEALTHY -> FAILED, predecessor SUPERSEDED -> DEPLOYING), so a pulled
 * deployment can never exist without the row explaining it. `actorUserId` is
 * null — the tenant agent's crash report is a system trigger.
 *
 * ## Why this is not DEPLOYMENT_FAILED
 *
 * It was, with `priorStatus: HEALTHY`. That made the post-cutover rollback the
 * one rollback path with no name of its own, and it rendered identically to a
 * deploy that never made it live. Those demand different responses: a build
 * that never shipped is a bad push, a serving version pulled back is a
 * regression that reached users. The event NAME is what a watcher, a dashboard
 * filter and a scanning operator all read first, and it was saying the wrong
 * one of the two.
 *
 * Distinct from `DEPLOYMENT_ROLLED_BACK_HEALTH_CHECK`, its PRE-cutover twin:
 * that records a deploy whose own health check failed before it took traffic,
 * so the prior version kept serving throughout and nothing regressed for a
 * user. Here the new version DID serve, and the old container is already
 * purged — which is why the predecessor has to be re-placed rather than simply
 * left alone.
 *
 * ## Why the restored pair is nullable
 *
 * A first-ever deployment can crash-loop with no healthy predecessor to
 * restore. That case emits this event too, with both fields null, rather than
 * falling back to `DEPLOYMENT_FAILED`: a serving app pulled with nothing left
 * to replace it is the MOST severe outcome on this path, and the fallback
 * would file it under the least severe bucket — the exact ambiguity this event
 * exists to remove. The null pair is the "nothing is serving" signal, readable
 * from the row alone; `errorReason` says it in words for a human.
 */
export const VibeAuditDeploymentRolledBackCrashLoopPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_ROLLED_BACK_CRASH_LOOP"),
  /// The crashing deployment, flipped HEALTHY -> FAILED — the version pulled
  /// OUT of service. Named `vibeDeploymentId` rather than a `crashed*` prefix
  /// so the feed's generic deployment-id read finds it like every other
  /// deployment-subject payload.
  vibeDeploymentId: z.string().uuid(),
  /// The status the crasher held when the agent reported it. HEALTHY on the
  /// only path that writes this today — the entity's `markPulledFromService`
  /// source-guard is the enforcement layer — but recorded rather than assumed,
  /// mirroring DEPLOYMENT_FAILED.
  priorStatus: z.enum(DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES),
  /// The customer-visible restore narrative, carrying the container exit code
  /// and the capped stderr tail the agent sent. No max length — capping would
  /// turn a crash into a 500 on a long stderr tail. Non-empty per the entity
  /// contract (`markPulledFromService` requires `errorReason`).
  errorReason: z.string().min(1),
  /// Commit sha of the version that crash-looped, so the user can fix and
  /// re-trigger without log-scraping.
  triggerSha: z.string().min(1).max(64),
  /// The predecessor re-activated SUPERSEDED -> DEPLOYING — the version now
  /// coming back. NULL when no healthy version ever preceded the crasher, the
  /// one case where the app is legitimately left with nothing serving.
  restoredDeploymentId: z.string().uuid().nullable(),
  /// Commit sha of the restored version, null when there was no predecessor.
  /// The writer sets this and `restoredDeploymentId` from one value, but the
  /// pairing is NOT enforceable here — `discriminatedUnion` accepts ZodObject
  /// members only and `.refine()` yields ZodEffects, so there is nowhere to
  /// assert it. Nothing depends on it: each reader narrows on the field it
  /// actually consumes (the feed's timeline routing on the id, the console's
  /// copy on the sha), so neither can be broken by the other being null.
  restoredTriggerSha: z.string().min(1).max(64).nullable()
});
export type VibeAuditDeploymentRolledBackCrashLoopPayload = z.infer<
  typeof VibeAuditDeploymentRolledBackCrashLoopPayloadSchema
>;

/**
 * Emitted when a deployment that was ALREADY SERVING failed its post-deploy
 * smoke — the operator's own named routes answered WRONG on the build the edge
 * had just been proved to be serving — and the platform pulled it, restoring its
 * last-healthy predecessor. Written INSIDE the same composite `$transaction` as
 * both status flips (smoke-failed HEALTHY -> FAILED, predecessor SUPERSEDED ->
 * DEPLOYING), so a pulled deployment can never exist without the row explaining
 * it. `actorUserId` is null — a sweep noticed, nobody asked.
 *
 * ## Why this is not any of the four existing rollback causes
 *
 * Each of them asserts something FALSE here, and the event NAME is what a
 * watcher, a dashboard filter and a scanning operator all read first:
 *   - `_CRASH_LOOP` names a container restart limit that was never hit. This
 *     container is healthy and answering — it is answering WRONGLY, which is a
 *     different incident with a different fix.
 *   - `_USER` asserts a deliberate human action, and no human acted.
 *   - `_HEALTH_CHECK` is pinned as the PRE-cutover twin, where the prior version
 *     kept serving and nothing regressed for a user. This is post-cutover.
 *   - `_COST_SAFETY` is a policy action, not a fault.
 * #2389 already paid to split `_CRASH_LOOP` out of `DEPLOYMENT_FAILED` for
 * exactly this reason; borrowing one here would recreate that defect.
 *
 * ## Why the route fields are on the row
 *
 * They are the EVIDENCE, and without them the row asserts a teardown with
 * nothing an operator can act on. `errorReason` says it in prose for the
 * console; `smokeRouteName` / `smokeRoutePath` are the machine-readable pair a
 * dashboard can group by, so "which route keeps pulling deploys" is answerable
 * without parsing a sentence.
 *
 * ## Why the restored pair is nullable
 *
 * Identical to `_CRASH_LOOP`: an app whose FIRST deployment fails its smoke has
 * no healthy predecessor, and the null pair is the "nothing is serving" signal
 * readable from the row alone. That case is the most severe outcome on this
 * path, so it keeps this event type rather than falling back to a milder one.
 */
export const VibeAuditDeploymentRolledBackFailedSmokePayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_ROLLED_BACK_FAILED_SMOKE"),
  /// The deployment pulled OUT of service, flipped HEALTHY -> FAILED. Named
  /// `vibeDeploymentId` rather than a `pulled*` prefix so the feed's generic
  /// deployment-id read finds it like every other deployment-subject payload.
  vibeDeploymentId: z.string().uuid(),
  /// The status the pulled deployment held. HEALTHY on the only path that writes
  /// this — the sweep only ever smokes a HEALTHY row — but recorded rather than
  /// assumed, mirroring `_CRASH_LOOP` and `DEPLOYMENT_FAILED`.
  priorStatus: z.enum(DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES),
  /// The customer-visible narrative: which route failed, what was wrong with its
  /// answer, and whether a previous version was restored. Non-empty per the
  /// entity contract.
  errorReason: z.string().min(1),
  /// Commit sha of the version that failed its smoke, so the operator can fix
  /// and re-trigger without log-scraping.
  triggerSha: z.string().min(1).max(64),
  /// The operator's own label for the route that failed — their vocabulary, not
  /// a path they have to map back by hand.
  smokeRouteName: z.string().min(1),
  /// The path that was requested, verbatim.
  smokeRoutePath: z.string().min(1),
  /// How many CONSECUTIVE probe rounds that route failed before the platform
  /// acted — the flap threshold that was actually met, recorded so a reader can
  /// tell a decisive failure from a borderline one without knowing what the
  /// app's threshold was set to at the time.
  consecutiveFailures: z.number().int().positive(),
  /// The predecessor re-activated SUPERSEDED -> DEPLOYING — the version now
  /// coming back. NULL when no healthy version ever preceded this one, the one
  /// case where the app is legitimately left with nothing serving.
  restoredDeploymentId: z.string().uuid().nullable(),
  /// Commit sha of the restored version, null when there was no predecessor.
  /// Same non-enforceable pairing as `_CRASH_LOOP`: `discriminatedUnion` accepts
  /// ZodObject members only, so there is nowhere to assert it. Each reader
  /// narrows on the field it consumes, so neither breaks by the other being null.
  restoredTriggerSha: z.string().min(1).max(64).nullable()
});
export type VibeAuditDeploymentRolledBackFailedSmokePayload = z.infer<
  typeof VibeAuditDeploymentRolledBackFailedSmokePayloadSchema
>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
const VibeAuditRolledBackPriorStatusRead = { priorStatus: VibeAuditDeploymentStatusReadSchema };

export const VibeAuditDeploymentRolledBackCostSafetyReadPayloadSchema =
  VibeAuditDeploymentRolledBackCostSafetyPayloadSchema.extend({
    priorStatus: VibeAuditCostSafetyActiveStatusReadSchema
  });
export const VibeAuditDeploymentRolledBackHealthCheckReadPayloadSchema =
  VibeAuditDeploymentRolledBackHealthCheckPayloadSchema.extend(VibeAuditRolledBackPriorStatusRead);
export const VibeAuditDeploymentRolledBackCrashLoopReadPayloadSchema =
  VibeAuditDeploymentRolledBackCrashLoopPayloadSchema.extend(VibeAuditRolledBackPriorStatusRead);
export const VibeAuditDeploymentRolledBackFailedSmokeReadPayloadSchema =
  VibeAuditDeploymentRolledBackFailedSmokePayloadSchema.extend(VibeAuditRolledBackPriorStatusRead);
